import React, {Fragment, useContext, useEffect} from 'react';
import {ApiUtility} from "../common/ApiUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {ListPaginationOptions} from "../common/ListPaginationOptions";
import {UpdateSubscription} from "./UpdateSubscription";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {CommonListPageHeader} from "../common/CommonListPageHeader";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {UrlUtility} from "../common/UrlUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {PillButton} from "../common/PillButton";
import {SubscriptionDataUtility} from "../common/SubscriptionDataUtility";
import {InvoiceDataUtility} from "../common/InvoiceDataUtility";
import {SingleInvoice} from "../invoices/SingleInvoice";
import {ConfirmCancelModal} from "../common/ConfirmCancelModal";

export const SubscriptionInvoicesListPage = function (props) {
    const context = useContext(GlobalContext);
    const urlBase = '/subscription-invoices';
    const renderableData = useRenderableData({
        selectedPage: 1,
        searchText: '',
        showUpdateSubscriptionModal: false,
        updateSubscriptionId: null,
        showUpdateInvoiceModal: false,
        showConfirmSyncModal: false,
        updateInvoiceId: null,
        invoiceList: [],
        pageCount: 0,
        loading: false,

        prevUrlQueryString: null,
        prevParams: null,
    })

    // Update state if URL changes.
    if (
        (props.location?.search) !== renderableData.prevUrlQueryString ||
        (props.match?.params) !== renderableData.prevParams
    ) {
        let selectedPage = 1;
        let searchText = '';
        let updateSubscriptionId = null;
        let showUpdateSubscriptionModal = false;
        let updateInvoiceId = null;
        let showUpdateInvoiceModal = false;

        let routeParams = props.match && props.match.params;
        if (routeParams?.subscriptionId) {
            showUpdateSubscriptionModal = true;
            updateSubscriptionId = routeParams['subscriptionId'];
        }

        if (routeParams?.invoiceId) {
            showUpdateInvoiceModal = true;
            updateInvoiceId = routeParams.invoiceId;
        }

        let queryParams = UrlUtility.getQueryParamsFromProps(props);
        if (queryParams.page) {
            selectedPage = queryParams.page;
        }

        if (queryParams.searchText) {
            searchText = queryParams.searchText;
        }

        renderableData.updateAndRender({
            selectedPage: selectedPage,
            searchText: searchText,
            showUpdateSubscriptionModal: showUpdateSubscriptionModal || false,
            updateSubscriptionId: updateSubscriptionId,

            showUpdateInvoiceModal: showUpdateInvoiceModal || false,
            updateInvoiceId: updateInvoiceId,

            prevUrlQueryString: props.location?.search,
            prevParams: props.match && props.match.params,
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadList();
        },
        [
            '' + renderableData.selectedPage,
            '' + renderableData.searchText,
            JSON.stringify(renderableData.filters)
        ]
    );

    function reloadList(callback) {
        renderableData.updateAndRender({loading: true})
        ApiUtility.getSubscriptionInvoicesList(
            renderableData.selectedPage,
            renderableData.searchText,
            renderableData.filters
        )
            .then(function (listData) {
                renderableData.updateAndRender({
                    ...listData,
                    loading: false
                });
                if (callback) {
                    callback();
                }
            })
            .catch(function (error) {
                renderableData.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the subscription invoice list.`);
            });
    }

    function goToUpdateSubscriptionModal(subscriptionId) {
        context.navigator.navigateTo(`${urlBase}/subscriptions/${subscriptionId}${props.location.search}`);
    }

    function goToUpdateInvoiceModal(invoiceId) {
        context.navigator.navigateTo(`${urlBase}/${invoiceId}${props.location.search}`);
    }

    function goToSubscriptionsListPage() {
        context.navigator.navigateTo("/subscriptions");
    }

    function closeModals() {
        context.navigator.navigateTo(`${urlBase}${props.location.search}`);
    }

    function afterSuccessfulSubscriptionSave(subscriptionId) {
        closeModals()
        reloadList(
            () => goToUpdateSubscriptionModal(subscriptionId)
        );
    }

    function afterSuccessfulInvoiceSave(invoiceId) {
        closeModals()
        reloadList(
            () => goToUpdateInvoiceModal(invoiceId)
        );
    }

    function onClickSync() {
        renderableData.updateAndRender({showConfirmSyncModal: true})
    }

    function closeConfirmSyncModal() {
        renderableData.updateAndRender({showConfirmSyncModal: false})
    }

    function performSync() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.syncAllSubscriptionInvoices()
            .then((response) => {
                closeConfirmSyncModal();
                MessageDisplayerUtility.success(`Successfully synced subscription invoices.`);
                reloadList();
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while syncing the subscription invoices.`);
            });
    }

    function renderInvoicesListDisplay(invoicesList) {
        let invoicesListDisplay = [];

        for (let i = 0; i < invoicesList.length; i++) {
            const invoice = invoicesList[i];

            let description = invoice.subscription.description || '';
            if (description.length > 200) {
                description = description.substring(0, 200) + '...';
            }

            let totalDisplay = SubscriptionDataUtility.getSubscriptionTotalDisplay(invoice.subscription)
            let linesDisplay = SubscriptionDataUtility.getSubscriptionLinesDisplay(invoice.subscription)

            let invoiceStatusClass = InvoiceDataUtility.invoiceStatusToClass(invoice.invoice_status)

            invoicesListDisplay.push(
                <div
                    key={i + 1}
                    id={`subscriptionInvoiceListRow-${invoice.id}`}
                    className="common-list-row common-list-values-row subscription-invoices-list-row"
                    onClick={() => goToUpdateInvoiceModal(invoice.id)}
                >
                    <div className="common-list-row-content">
                        <div
                            className="common-list-row-cell common-list-row-value-cell subscription-invoices-list-row-cell basics"
                        >
                            <div>
                                <div>
                                    <span>
                                        <span className="invoice-title">
                                            {`Invoice #${invoice.sequence_number_in_subscription}`}
                                        </span>
                                        <span className="invoice-customer">
                                            {` - ${invoice.subscription.ext_stripe_customer_name}`}
                                        </span>
                                    </span>
                                </div>
                                <div>
                                    <span className="invoice-subscription-basics">
                                        <span className="subscription-invoice-total-display">{totalDisplay}</span>
                                        <span className="subscription-invoice-lines-summary">{linesDisplay}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell subscription-invoices-list-row-cell subscription-description"
                        >{description}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell subscription-invoices-list-row-cell status"
                        >
                            <div className="invoice-status-content">
                                <div className={`invoice-status-badge ${invoiceStatusClass}`}>
                                    {invoice.invoice_status.name}
                                </div>
                                <div className="invoice-status-additional-info">
                                    {invoice.invoice_status.additional_info}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return invoicesListDisplay;
    }

    if (renderableData.invoiceList) {
        return (
            <div className="common-list-page subscription-invoices-list-page">
                <CommonListPageHeader
                    tabs={[
                        {
                            title: 'Subscriptions',
                            onClick: goToSubscriptionsListPage,
                            selected: false
                        },
                        {
                            title: 'Subscription Invoices',
                            onClick: () => null,
                            selected: true
                        },
                    ]}
                    rightHeaderRenderer={() => (
                        <Fragment>
                            <PillButton
                                onClick={onClickSync}
                                containerClasses="sync-button-container"
                                buttonClasses="sync-button"
                                leftIconSrc={"/static/images/sync-white.svg"}
                                buttonText="SYNC ALL"
                            />
                        </Fragment>
                    )}
                />

                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={renderableData.loading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    {(() => {
                        if (renderableData.invoiceList.length > 0) {
                            return (
                                <Fragment>
                                    <div className="common-list-header">
                                        <div className="common-list-row common-list-labels-row">
                                            <div className="common-list-row-content">
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscription-invoices-list-row-cell basics"
                                                >Basics
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscription-invoices-list-row-cell subscription-description"
                                                >Subscription Description
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscription-invoices-list-row-cell status"
                                                >Status
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="common-list-content">
                                        {renderInvoicesListDisplay(renderableData.invoiceList)}
                                    </div>
                                    <ListPaginationOptions
                                        pageCount={renderableData.pageCount}
                                        selectedPage={renderableData.selectedPage}
                                        urlBase="/subscriptions"
                                    />
                                </Fragment>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No subscription invoices found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
                {(() => {
                    if (renderableData.showUpdateSubscriptionModal) {
                        return (
                            <UpdateSubscription
                                cancel={closeModals}
                                afterSuccessfulSave={afterSuccessfulSubscriptionSave}
                                subscriptionId={renderableData.updateSubscriptionId}
                            />
                        );
                    } else if (renderableData.showUpdateInvoiceModal) {
                        return (
                            <SingleInvoice
                                cancel={closeModals}
                                invoiceId={renderableData.updateInvoiceId}
                                afterSuccessfulSave={afterSuccessfulInvoiceSave}
                                goToSubscription={goToUpdateSubscriptionModal}
                            />
                        );
                    } else if (renderableData.showConfirmSyncModal) {
                        return (
                            <ConfirmCancelModal
                                cancel={closeConfirmSyncModal}
                                confirm={performSync}
                                headerText="Confirm Sync"
                                confirmButtonText="SYNC"
                                warningMessage={[
                                    "Are you sure you want to sync all subscription invoices?",
                                    "This will sync any new Stripe subscription invoices from the last 30 days to our system and QBO."
                                ]}
                            />
                        )
                    }
                })()}
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};
